import dayjs from 'dayjs'
import Image from 'next/image'
import { useState } from 'react'
import { Colors } from '@kvika/audur-theme'

import { formatNumberIS } from '../../../utils/Utils'
import { LoanCalculationCalculatedData, PaymentPlanInstallment } from '../newCalculator.models'

import styles from './newCalculatorContainers.module.scss'
import NewCalculatorLineChart from './newCalculatorLineChart'

type INewCalculatorPaymentPlanProps = {
	paymentPlanProps?: LoanCalculationCalculatedData
	title: string
	isPaymentReductionPlan?: boolean
}

const NewCalculatorPaymentPlan = (props: INewCalculatorPaymentPlanProps) => {
	const [isExpanded, setIsExpanded] = useState(false)
	const { paymentPlanProps, title, isPaymentReductionPlan = false } = props

	if (paymentPlanProps === undefined) {
		return null
	}

	const chartColor = isPaymentReductionPlan ? Colors.LightOrange : Colors.Green

	const paymentPlanTableHeaders: { title: string; value: keyof PaymentPlanInstallment; currency?: boolean }[] = [
		{ title: 'Greiðsla', value: 'number' },
		{ title: 'Dagsetning', value: 'dueDate' },
		{ title: 'Afborgun', value: 'principal', currency: true },
		{ title: 'Vextir', value: 'interest', currency: true },
		{ title: 'Greiðslugjald', value: 'paymentFee', currency: true },
		{ title: 'Greiðsla samtals', value: 'total', currency: true },
	]

	return (
		<div className={styles.paymentPlan}>
			<div className={styles.title}>{title}</div>
			<div className={styles.grid}>
				<div className={styles.chart}>
					<NewCalculatorLineChart
						data={paymentPlanProps.monthlyInstallementChartData}
						title='Mánaðarleg afborgun'
						color={chartColor}
					/>
				</div>
				<div className={styles.chart}>
					<NewCalculatorLineChart
						data={paymentPlanProps.principalReductionChartData}
						title='Lækkun höfuðstóls'
						color={chartColor}
					/>
				</div>
			</div>
			<div className={styles.tableScrollWrapper}>
				<div className={styles.table}>
					<div className={styles.tableHeaders}>
						{paymentPlanTableHeaders.map((header) => (
							<div className={styles.title} key={header.title}>
								{header.title}
							</div>
						))}
					</div>
					{paymentPlanProps &&
						paymentPlanProps.installments &&
						paymentPlanProps.installments.map((row, rowIndex) => {
							const isWithinFirstFive = rowIndex < 5
							const isLastFive = rowIndex >= paymentPlanProps.installments.length - 5
							const shouldDisplay = isExpanded || isWithinFirstFive || isLastFive

							if (!isExpanded && rowIndex === 5) {
								return (
									<div className={styles.tableRowExpand} key='expand-button'>
										{}
										<div
											onClick={() => {
												setIsExpanded(true)
											}}
										>
											<Image
												src='/svg/chevron.svg'
												width={27}
												height={27}
												alt='ChevronBottom'
												className={styles.chevronBottom}
											/>
											Sýna alla gjalddaga
										</div>
									</div>
								)
							}

							if (!shouldDisplay) {
								return null
							}

							return (
								<div className={styles.tableRow} key={row.dueDate}>
									{paymentPlanTableHeaders.map(({ value, currency }, index) => {
										const cellValue = row[value]

										if (cellValue === undefined) {
											return null
										}

										const isNumber = typeof cellValue === 'number'
										const isDate = value === 'dueDate'

										return (
											<div className={styles.rowCell} key={`${rowIndex.toString()}/${index.toString()}`}>
												{isNumber
													? currency
														? `${formatNumberIS(cellValue)} kr.`
														: formatNumberIS(cellValue)
													: isDate
														? dayjs(cellValue).format('DD.MM.YYYY')
														: cellValue}
											</div>
										)
									})}
								</div>
							)
						})}
				</div>
			</div>
		</div>
	)
}

export default NewCalculatorPaymentPlan
